// src/App.tsx
import { useEffect, useRef, useState } from "react";
import LabelSheetPanel from "./components/labels/LabelSheetPanel";
import { type CodeItem } from "./lib/export/pdfSheet";
import Papa from "papaparse";
import JSZip from "jszip";
import "./App.css";
import { useTheme } from "./lib/useTheme";
import { useLicenseContext } from "./lib/LicenseContext";
import Link from "./components/Link";
import OutputOptionsPanel from "./components/OutputOptionsPanel";
import type { StyledOptions, ModuleShape, EyeOuterShape, EyeInnerShape } from "./lib/styledQr/renderSvg";
import { type FrameKey } from "./lib/frames";
import { getDefaultPreset, getPresetByKey } from "./lib/labels";
import { renderQrArtwork } from "./lib/renderQrArtwork";
import { injectWhiteBackground } from "./lib/svg";
import { makePdfSheet } from "./lib/export/pdfSheet";
import { ADS_ENABLED } from "./lib/ads"; // disabled for AdSense review readiness

/* ---------- Types ---------- */
type Row = { url: string };
type ECC = "L" | "M" | "Q" | "H";
type RenderOpts = {
  ecc: ECC;
  margin: number;
  pngSize: number;
  asPNG: boolean;
  logo?: { dataURL: string; pct: number; backer: boolean; isSVG: boolean };
};

const FREE_LIMIT = 20;

/* ---------- AdSense config (replace with your real IDs) ---------- */
const ADSENSE_CLIENT = "ca-pub-4084661741366623"; // <-- your AdSense client id
const ADSENSE_SLOT_BOX = "9876543210";            // <-- 300x250/336x280 unit id
const ADSENSE_SLOT_BANNER = "1122334455";         // <-- TODO: banner unit id (we reserve 64px high)

/* Utility: load AdSense script once, lazily */
let _adsenseLoaded = false;
function loadAdSenseScript(): Promise<void> {
  // Ads are disabled by default for AdSense review readiness.
  // When off, do not inject any AdSense scripts.
  if (!ADS_ENABLED) return Promise.resolve();
  if (_adsenseLoaded) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]') as HTMLScriptElement | null;
    if (existing) {
      _adsenseLoaded = true;
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";
    s.onload = () => { _adsenseLoaded = true; resolve(); };
    s.onerror = () => reject(new Error("adsense-load-failed"));
    document.head.appendChild(s);
  });
}

/* ---------- AdSense card (under preview) ---------- */
function AdSenseBoxAd({
  client,
  slot,
  prefer336 = true,
}: { client: string; slot: string; prefer336?: boolean }) {
  // Ads are disabled by default for AdSense review readiness.
  // When off, do not render any ad container markup.
  const adsEnabled = ADS_ENABLED;
  const hostRef = useRef<HTMLDivElement | null>(null);
  const insRef = useRef<HTMLModElement | null>(null);
  const [inView, setInView] = useState(false);

  // Reserve a fixed box to avoid CLS (300x250 or 336x280)
  // We keep CSS-driven sizing with .ad--box and optional .ad--box-336
  useEffect(() => {
    if (!adsEnabled) return;
    const el = hostRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "200px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!adsEnabled) return;
    if (!inView || !insRef.current) return;
    let cancelled = false;
    loadAdSenseScript()
      .then(() => {
        if (cancelled || !insRef.current) return;
        try {
          // request fill (fixed size to avoid CLS)
          // @ts-ignore
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch {}
      })
      .catch(() => {/* fail silently; house fallback not needed here since size is reserved */});
    return () => { cancelled = true; };
  }, [inView]);

  if (!adsEnabled) return null;
  return (
    <div ref={hostRef} className="preview-panel-ad">
      <div className={`ad-slot ad--box ${prefer336 ? "ad--box-336" : ""}`} style={{ position: "relative" }}>
        {!inView && (
          <div className="ad-skeleton" aria-hidden="true">
            <div className="ad-skeleton__shimmer" />
          </div>
        )}
        {inView && (
          <ins
            ref={insRef}
            className="adsbygoogle"
            style={{ display: "block", width: "100%", height: "100%" }}
            data-ad-client={client}
            data-ad-slot={slot}
            data-full-width-responsive="false"
          />
        )}
      </div>
    </div>
  );
}

/* ---------- Sticky bottom banner (overlay) ---------- */
function AdSenseStickyBanner({
  client,
  slot,
  frequency = "daily", // "session" | "daily"
  height = 64,
}: { client: string; slot: string; frequency?: "session" | "daily"; height?: number }) {
  // Ads are disabled by default for AdSense review readiness.
  // When off, do not render any ad container markup.
  const adsEnabled = ADS_ENABLED;
  const [visible, setVisible] = useState<boolean>(() => {
    try {
      if (frequency === "session") {
        return sessionStorage.getItem("printqr.banner.session") !== "1";
      }
      const v = localStorage.getItem("printqr.banner.dismissedAt");
      if (!v) return true;
      const then = new Date(parseInt(v, 10));
      const now = new Date();
      return !(then.getFullYear() === now.getFullYear() && then.getMonth() === now.getMonth() && then.getDate() === now.getDate());
    } catch { return true; }
  });
  const insRef = useRef<HTMLModElement | null>(null);
  const [bottomOffset, setBottomOffset] = useState(0);

  // Keyboard-aware offset + safe-area
  useEffect(() => {
    if (!adsEnabled) return;
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    if (!vv) return;
    const onVV = () => {
      const delta = Math.max(0, window.innerHeight - vv.height);
      setBottomOffset(delta);
    };
    vv.addEventListener("resize", onVV);
    vv.addEventListener("scroll", onVV);
    onVV();
    return () => {
      vv.removeEventListener("resize", onVV);
      vv.removeEventListener("scroll", onVV);
    };
  }, []);

  useEffect(() => {
    if (!adsEnabled) return;
    if (!visible || !insRef.current) return;
    let cancelled = false;
    loadAdSenseScript()
      .then(() => {
        if (cancelled || !insRef.current) return;
        try {
          // @ts-ignore
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch {}
      })
      .catch(() => {/* ignore */});
    return () => { cancelled = true; };
  }, [visible]);

  const dismiss = () => {
    try {
      if (frequency === "session") sessionStorage.setItem("printqr.banner.session", "1");
      else localStorage.setItem("printqr.banner.dismissedAt", String(Date.now()));
    } catch {}
    setVisible(false);
  };

  if (!adsEnabled || !visible) return null;

  return (
    <>
      {/* Spacer only on small screens so controls aren't covered (class present in App.css additions) */}
      <div className="sticky-banner-spacer sm:hidden" style={{ height }} aria-hidden="true" />
      <div
        className="sticky-banner"
        role="complementary"
        aria-label="Advertisement banner"
        style={{ bottom: `max(env(safe-area-inset-bottom), ${bottomOffset}px)`, height }}
      >
        <div className="sticky-banner__inner">
          <div className="ad-slot ad--banner" style={{ position: "relative", height: "100%" }}>
            <ins
              ref={insRef}
              className="adsbygoogle"
              style={{ display: "block", width: "100%", height: "100%" }}
              data-ad-client={client}
              data-ad-slot={slot}
              data-full-width-responsive="false"
            />
          </div>
          <button type="button" className="sticky-banner__close" aria-label="Dismiss advertisement" onClick={dismiss}>×</button>
        </div>
      </div>
    </>
  );
}

/* ---------- App ---------- */
// Main QR generator app component
export default function App() {
  const { theme, toggleTheme } = useTheme();

  // Define all state hooks at the top level
  // No conditional returns or routing logic in this component
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>("");
  const [labelsBusy, setLabelsBusy] = useState(false);
  const [labelsMsg, setLabelsMsg] = useState<string>("");
  const [openSection, setOpenSection] = useState<number>(1);
  const { isPro: hasLicense, startCheckout } = useLicenseContext();
  const [ecc, setECC] = useState<ECC>("M");
  const [margin, setMargin] = useState(2);
  const [asPNG, setAsPNG] = useState(false);
  const [pngSize, setPngSize] = useState(1024);
  const [moduleShape, setModuleShape] = useState<ModuleShape>("square");
  const [roundness, setRoundness] = useState(0.35);
  const [eyeOuterShape, setEyeOuterShape] = useState<EyeOuterShape>("square");
  const [eyeInnerShape, setEyeInnerShape] = useState<EyeInnerShape>("square");
  const [colors, setColors] = useState<StyledOptions["colors"]>({
    background: "#FFFFFF",
    modules: "#000000",
    eyeOuter: "#000000",
    eyeInner: "#000000",
  });
  const logoFileRef = useRef<HTMLInputElement | null>(null);
  const [logoPct, setLogoPct] = useState(18); // Medium size (18%)
  const [logoBacker, setLogoBacker] = useState(true);
  const [logoDataURL, setLogoDataURL] = useState<string | null>(null);
  const [logoIsSVG, setLogoIsSVG] = useState<boolean>(false);
  const [previewSRC, setPreviewSRC] = useState<string>("");
  const [frame, setFrame] = useState<FrameKey>('none');
  const [frameColor, setFrameColor] = useState<string>("#000000");
  const frameSelected = frame !== 'none';
  const [selectedLabelKey, setSelectedLabelKey] = useState<string | null>(null);
  const [userOverrodeLabel, setUserOverrodeLabel] = useState<boolean>(false);

  // Ads are disabled on the App (tool) view to avoid "no publisher-content" policy issues.
  // We intentionally gate ads to show only on content pages (landing/blog/docs) in production.
  const shouldShowAds = false;

  const effectiveLimit = hasLicense ? rows.length : Math.min(rows.length, FREE_LIMIT);

  // Preview state
  const previewURL = rows[0]?.url || "https://printqr.app/demo/preview"; // render as PNG for reliability
  // Auto default label preset when frame toggles (unless user manually chose one)
  useEffect(() => {
    const nextKind = frameSelected ? 'framed' : 'square';
    if (!userOverrodeLabel) {
      const def = getDefaultPreset(nextKind);
      setSelectedLabelKey(def.key);
    }
  }, [frameSelected]);

  // Safety hint for scanning when using center logo
  const riskyLogo = Boolean(logoDataURL) && hasLicense && (ecc === "L" || ecc === "M" || logoPct > 18);
  const riskyStyle = moduleShape === "dots" || roundness >= 0.4;
  const applySafe = () => {
    setECC("H");
    if (logoPct > 16) setLogoPct(16);
    if (margin < 4) setMargin(4);
  };

  // Contrast warning between modules and background
  const contrast = wcagContrastRatio(colors.modules, colors.background);
  const lowContrast = contrast > 0 && contrast < 2.5;

  /* ---------- CSV ---------- */
  const onFile = (file: File) => {
    setMsg("Parsing CSV…");
    Papa.parse<Row>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const clean = (res.data || [])
          .map((r: any) => ({ url: String(r.url || "").trim() }))
          .filter((r) => r.url.length > 0);
        setRows(clean);
        const count = hasLicense ? clean.length : Math.min(clean.length, FREE_LIMIT);
        const cap = !hasLicense && clean.length > FREE_LIMIT ? ` (free tier limited to first ${FREE_LIMIT} rows)` : "";
        setMsg(`Loaded ${count} row${count === 1 ? "" : "s"}${cap}.`);
      },
      error: (e) => setMsg(`CSV error: ${e.message}`),
    });
  };
  const pickCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
    e.currentTarget.value = "";
  };

  /* ---------- Logo ---------- */
  const pickLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isSupported = /^image\/(png|jpeg|svg\+xml|webp|gif)$/i.test(f.type);
    if (!isSupported) {
      setMsg("Please choose a PNG, JPG, SVG, WEBP, or GIF logo file.");
      e.currentTarget.value = "";
      return;
    }
    try {
      const data = await fileToDataURL(f);
      setLogoDataURL(data);
      setLogoIsSVG(/^data:image\/svg\+xml/i.test(data));
      setMsg("Logo loaded.");
    } catch {
      setMsg("Failed to load logo.");
    }
  };

  /* ---------- Live preview (unified SVG pipeline) ---------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const frameArg = frame === 'none' ? 'none'
          : frame === 'tabTop' ? 'frame_speech_above'
          : frame === 'tabBottom' ? 'frame_speech_below'
          : frame === 'arrow' ? 'frame_arrow_scanme'
          : 'frame_bottom_label';
        const out = await renderQrArtwork({
          data: previewURL,
          ecc,
          colors: {
            background: (hasLicense ? colors : PALETTES[0].colors).background,
            modules: (hasLicense ? colors : PALETTES[0].colors).modules,
            eyeOuter: (hasLicense ? colors : PALETTES[0].colors).eyeOuter,
            eyeInner: (hasLicense ? colors : PALETTES[0].colors).eyeInner,
          },
          shapes: {
            finder: hasLicense ? eyeOuterShape : 'square',
            center: hasLicense ? eyeInnerShape : 'square',
            data: hasLicense ? moduleShape : 'square',
          },
          roundness: hasLicense ? roundness : 0,
          logo: hasLicense && logoDataURL ? (logoIsSVG ? { svg: dataURLToString(logoDataURL), sizePct: clamp(logoPct, 10, 25), backer: logoBacker } : { pngDataUrl: logoDataURL, sizePct: clamp(logoPct, 10, 25), backer: logoBacker }) : null,
          frame: frameArg as any,
          frameColor,
          labelText: 'SCAN ME',
          userMargin: margin,
        });
        const previewSvg = injectWhiteBackground(out.svg, "#ffffff");
        const svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(previewSvg);
        if (!cancelled) setPreviewSRC(svgUrl);
      } catch {
        if (!cancelled) setPreviewSRC("");
      }
    })();
    return () => { cancelled = true; };
  }, [previewURL, ecc, margin, moduleShape, roundness, colors, eyeOuterShape, eyeInnerShape, logoDataURL, logoPct, logoBacker, logoIsSVG, hasLicense, frame, frameColor]);

  /* ---------- Generate ZIP (unified renderer) ---------- */
  const generateAll = async () => {
    if (rows.length === 0) {
      setMsg("Upload a CSV first.");
      return;
    }
    setBusy(true);
    setMsg("Rendering…");
    try {
      const zip = new JSZip();
      const slice = hasLicense ? rows : rows.slice(0, FREE_LIMIT);

      const opts: RenderOpts = {
        ecc,
        margin,
        pngSize,
        asPNG: hasLicense ? asPNG : false,
        logo:
          hasLicense && logoDataURL
            ? { dataURL: logoDataURL, pct: clamp(logoPct, 10, 25), backer: logoBacker, isSVG: logoIsSVG }
            : undefined,
      };

      let idx = 1;
      for (const { url } of slice) {
        const stem = pad4(idx++);
        const frameArg = frame === 'none' ? 'none'
          : frame === 'tabTop' ? 'frame_speech_above'
          : frame === 'tabBottom' ? 'frame_speech_below'
          : frame === 'arrow' ? 'frame_arrow_scanme'
          : 'frame_bottom_label';

        const out = await renderQrArtwork({
          data: url,
          ecc: opts.ecc,
          colors: {
            background: (hasLicense ? colors : PALETTES[0].colors).background,
            modules: (hasLicense ? colors : PALETTES[0].colors).modules,
            eyeOuter: (hasLicense ? colors : PALETTES[0].colors).eyeOuter,
            eyeInner: (hasLicense ? colors : PALETTES[0].colors).eyeInner,
          },
          shapes: {
            finder: hasLicense ? eyeOuterShape : 'square',
            center: hasLicense ? eyeInnerShape : 'square',
            data: hasLicense ? moduleShape : 'square',
          },
          roundness: hasLicense ? roundness : 0,
          logo: opts.logo ? (opts.logo.isSVG ? { svg: dataURLToString(opts.logo.dataURL), sizePct: opts.logo.pct, backer: opts.logo.backer } : { pngDataUrl: opts.logo.dataURL, sizePct: opts.logo.pct, backer: opts.logo.backer }) : null,
          frame: frameArg as any,
          frameColor,
          labelText: 'SCAN ME',
          userMargin: opts.margin,
        });

        zip.file(`${stem}.svg`, out.svg);
        if (opts.asPNG) {
          const canvas = await svgToCanvas(out.svg, opts.pngSize);
          const blob = await canvasToBlob(canvas);
          zip.file(`${stem}.png`, blob);
        }
      }

      const blob = await zip.generateAsync({ type: "blob" });
      triggerDownload(blob, "qr.zip");
      setMsg(`Done. Exported ${slice.length} QR code${slice.length === 1 ? "" : "s"}.`);
    } catch (err: any) {
      console.error(err);
      setMsg(`Error: ${err?.message || "Failed to render."}`);
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Generate Labels PDF (Pro) ---------- */
  const generateLabels = async (ui: { presetKey: string; innerPaddingPct: number; startPosition: number; includeCaption: boolean }) => {
    if (!hasLicense) {
      setMsg("Labels are a Pro feature. Unlock with lifetime license.");
      return;
    }
    if (rows.length === 0) {
      setMsg("Upload a CSV first.");
      return;
    }
    setLabelsBusy(true);
    setLabelsMsg("Preparing…");
    try {
      const slice = hasLicense ? rows : rows.slice(0, FREE_LIMIT);

      // Render final SVGs for each row via shared renderer
      const items: CodeItem[] = [];
      let idx = 1;
      for (const { url } of slice) {
        const stem = pad4(idx++);
        const frameArg = frame === 'none' ? 'none'
          : frame === 'tabTop' ? 'frame_speech_above'
          : frame === 'tabBottom' ? 'frame_speech_below'
          : frame === 'arrow' ? 'frame_arrow_scanme'
          : 'frame_bottom_label';
        const out = await renderQrArtwork({
          data: url,
          ecc,
          colors: {
            background: (hasLicense ? colors : PALETTES[0].colors).background,
            modules: (hasLicense ? colors : PALETTES[0].colors).modules,
            eyeOuter: (hasLicense ? colors : PALETTES[0].colors).eyeOuter,
            eyeInner: (hasLicense ? colors : PALETTES[0].colors).eyeInner,
          },
          shapes: {
            finder: hasLicense ? eyeOuterShape : 'square',
            center: hasLicense ? eyeInnerShape : 'square',
            data: hasLicense ? moduleShape : 'square',
          },
          roundness: hasLicense ? roundness : 0,
          logo: logoDataURL ? (logoIsSVG ? { svg: dataURLToString(logoDataURL), sizePct: clamp(logoPct, 10, 25), backer: logoBacker } : { pngDataUrl: logoDataURL, sizePct: clamp(logoPct, 10, 25), backer: logoBacker }) : null,
          frame: frameArg as any,
          frameColor,
          labelText: 'SCAN ME',
          userMargin: margin,
        });
        items.push({ name: `${stem}.svg`, svg: out.svg });
      }

      const preset = getPresetByKey(ui.presetKey) || getDefaultPreset(frameSelected ? 'framed' : 'square');
      const pdfBytes = await makePdfSheet(items, {
        preset,
        innerPaddingPct: ui.innerPaddingPct,
        startPosition: ui.startPosition,
        includeCaption: ui.includeCaption,
        frameKey: frame,
        frameColor: frameColor,
        onProgress: (p, t) => setLabelsMsg(`Placing labels page ${p}/${t}…`),
      });

      const blob = new Blob([pdfBytes as any], { type: "application/pdf" });
      triggerDownload(blob, "labels.pdf");
      setLabelsMsg("Done.");
    } catch (err: any) {
      console.error(err);
      setLabelsMsg(`Error: ${err?.message || "Failed to build PDF."}`);
    } finally {
      setLabelsBusy(false);
    }
  };

  /* ---------- UI ---------- */
  return (
    <div className="layout-container">
      <div className="container">
        {/* Top header bar */}
        <header className="header">
          <div className="flex items-center gap-4">
            <h1 className="font-extrabold tracking-tight">PrintQR — Bulk QR Generator</h1>
            <Link to="/" className="hidden text-sm underline-offset-4 hover:underline sm:inline">Home</Link>
            <Link to="/docs/getting-started" className="hidden text-sm underline-offset-4 hover:underline sm:inline">Docs</Link>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={toggleTheme} 
              className="btn btn-icon" 
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="4"></circle>
                  <path d="M12 2v2"></path>
                  <path d="M12 20v2"></path>
                  <path d="m4.93 4.93 1.41 1.41"></path>
                  <path d="m17.66 17.66 1.41 1.41"></path>
                  <path d="M2 12h2"></path>
                  <path d="M20 12h2"></path>
                  <path d="m6.34 17.66-1.41 1.41"></path>
                  <path d="m19.07 4.93-1.41 1.41"></path>
                </svg>
              )}
            </button>
              {hasLicense ? (
                <button className="btn btn-chip pro">Pro Lifetime: Active</button>
              ) : (
                <button 
                  onClick={startCheckout}
                  className="btn btn-chip"
                  title="Click to upgrade to Pro"
                >
                  Free Mode
                </button>
              )}
          </div>
        </header>

        {/* Main content */}
        <main>
          <div className="content-grid">
            {/* Left: Controls */}
            <div className="main-content">
              {/* CSV */}
              <details open={openSection === 1} className="section">
                <summary className="section-summary" onClick={(e) => { e.preventDefault(); setOpenSection(openSection === 1 ? 0 : 1); }}>1) Upload CSV</summary>
                <div className="section-body">
                  <p className="text-sm mb-4">Expected headers: <code>url</code>. Free tier processes first 20 rows.</p>
                  <Label className="form-label text-sm">
                    <span>CSV file</span>
                    <div className="file-input-container">
                      <input id="csv" type="file" accept=".csv,text/csv" onChange={pickCSV} className="file-input" />
                    </div>
                  </Label>
                  <div className="text-sm mt-3" aria-live="polite">
                    {rows.length > 0 ? (
                      <>
                        Loaded <b>{effectiveLimit}</b> row{effectiveLimit === 1 ? '' : 's'}
                        {!hasLicense && rows.length > FREE_LIMIT ? <> (showing first {FREE_LIMIT})</> : null}
                      </>
                    ) : (
                      <>No rows loaded.</>
                    )}
                  </div>
                </div>
              </details>

              {/* Options */}
              <details open={openSection === 2} className="section">
                <summary className="section-summary" onClick={(e) => { e.preventDefault(); setOpenSection(openSection === 2 ? 0 : 2); }}>2) Output Options</summary>
                <div className="section-body">
                  <OutputOptionsPanel
                    isPro={hasLicense}
                    opts={{
                      ecc,
                      marginModules: margin,
                      frameType: frame,
                      frameColor,
                      bodyColor: colors.modules,
                      bgColor: colors.background,
                      eyeBorderColor: colors.eyeOuter,
                      eyeCenterColor: colors.eyeInner,
                      moduleShape,
                      roundness,
                      eyeBorderShape: eyeOuterShape,
                      eyeCenterShape: eyeInnerShape,
                      exportPng: asPNG,
                      pngSize,
                    }}
                    lowContrast={lowContrast}
                    contrastRatio={contrast}
                    onChange={(patch) => {
                      if (patch.ecc) setECC(patch.ecc);
                      if (patch.marginModules !== undefined) setMargin(patch.marginModules);
                      if (patch.frameType) setFrame(patch.frameType as FrameKey);
                      if (patch.frameColor) setFrameColor(patch.frameColor);
                      if (
                        (patch as any).bodyColor !== undefined ||
                        (patch as any).bgColor !== undefined ||
                        (patch as any).eyeBorderColor !== undefined ||
                        (patch as any).eyeCenterColor !== undefined
                      ) {
                        setColors({
                          ...colors,
                          modules: (patch as any).bodyColor ?? colors.modules,
                          background: (patch as any).bgColor ?? colors.background,
                          eyeOuter: (patch as any).eyeBorderColor ?? colors.eyeOuter,
                          eyeInner: (patch as any).eyeCenterColor ?? colors.eyeInner,
                        });
                      }
                      if ((patch as any).moduleShape) setModuleShape((patch as any).moduleShape);
                      if ((patch as any).roundness !== undefined) setRoundness((patch as any).roundness);
                      if ((patch as any).eyeBorderShape) setEyeOuterShape((patch as any).eyeBorderShape);
                      if ((patch as any).eyeCenterShape) setEyeInnerShape((patch as any).eyeCenterShape);
                      if ((patch as any).exportPng !== undefined) setAsPNG((patch as any).exportPng);
                      if ((patch as any).pngSize !== undefined) setPngSize((patch as any).pngSize);
                    }}
                    onPreset={(name) => {
                      if (name === "Monochrome") {
                        setColors(LIGHT_PALETTE);
                        setFrameColor("#000000");
                        return;
                      }
                      if (name === "High Contrast") {
                        const hc = PALETTES.find(p => p.name === "High Contrast");
                        const next = hc ? hc.colors : LIGHT_PALETTE;
                        setColors(next);
                        setFrameColor(next.modules);
                        return;
                      }
                      if (name === "Brand Blue") {
                        // Keep body dark, add blue accent to eyes and frame
                        setColors({ ...colors, eyeOuter: "#2563EB" });
                        setFrameColor("#2563EB");
                        return;
                      }
                      if (name === "Inverted") {
                        const isDarkBackground = wcagContrastRatio(colors.background, "#000000") < wcagContrastRatio(colors.background, "#FFFFFF");
                        const next = isDarkBackground ? LIGHT_PALETTE : DARK_PALETTE;
                        setColors(next);
                        setFrameColor(next.modules);
                        return;
                      }
                    }}
                  />
                </div>
              </details>

              {/* Safety banner */}
              {(riskyLogo || riskyStyle) && (
                <div className="alert alert-warning">
                  <div>Large logo or low ECC can make codes unscannable. Try ECC=H, Logo ≤16%, Margin ≥4.</div>
                  <button 
                    onClick={applySafe} 
                    className="rounded-lg border border-[var(--warnBorder)] px-3 py-2 text-sm font-semibold text-[var(--warnText)] hover:bg-[rgba(250,204,21,0.2)]"
                  >
                    Apply safe settings
                  </button>
                </div>
              )}

              {/* Logo */}
              <details open={openSection === 3} className="section">
                <summary className="section-summary" onClick={(e) => { e.preventDefault(); setOpenSection(openSection === 3 ? 0 : 3); }}>3) Center Logo (Pro)</summary>
                <div className="section-body">
                  <div className="form-grid">
                    <div className={`${hasLicense ? '' : 'opacity-55'}`}>
                      <Label htmlFor="logo-file" className="form-label text-sm">
                        <span>Logo file</span>
                        <div className="form-control-wrapper">
                          <div className="file-input-wrapper">
                            <input
                              id="logo-file"
                              ref={logoFileRef}
                              type="file"
                              accept="image/*,image/svg+xml"
                              onChange={pickLogo}
                              disabled={!hasLicense}
                          title={hasLicense ? "" : "Unlock Lifetime Pro to enable"}
                              className="form-input"
                            />
                          </div>
                        </div>
                      </Label>
                      <div className="text-xs">PNG or SVG recommended. Pro enables PNG export and logo overlay.</div>
                    </div>
                    <div className={`${hasLicense ? '' : 'opacity-55'}`}>
                      <Label htmlFor="logo-size" className="form-label text-sm">
                        <span>Logo size</span>
                        <div className="form-control-wrapper">
                          <div className="logo-size-selector">
                            <button 
                              onClick={() => setLogoPct(15)} 
                              className={`btn ${logoPct === 15 ? 'btn-primary' : 'btn-outline'}`}
                              disabled={!hasLicense}
                          title={hasLicense ? "" : "Unlock Lifetime Pro to enable"}
                            >
                              Small
                            </button>
                            <button 
                              onClick={() => setLogoPct(18)} 
                              className={`btn ${logoPct === 18 ? 'btn-primary' : 'btn-outline'}`}
                              disabled={!hasLicense}
                          title={hasLicense ? "" : "Unlock Lifetime Pro to enable"}
                            >
                              Medium
                            </button>
                            <button 
                              onClick={() => setLogoPct(20)} 
                              className={`btn ${logoPct === 20 ? 'btn-primary' : 'btn-outline'}`}
                              disabled={!hasLicense}
                          title={hasLicense ? "" : "Unlock Lifetime Pro to enable"}
                            >
                              Large
                            </button>
                          </div>
                        </div>
                      </Label>
                      <div className="text-xs">Selected: {logoPct === 15 ? 'Small' : logoPct === 18 ? 'Medium' : 'Large'} ({logoPct}%)</div>
                    </div>
                    <Label className={`form-label text-sm ${hasLicense ? '' : 'opacity-55'}`}>
                      <div className="flex items-center gap-2 min-h-11">
                        <input
                          id="logo-backer"
                          type="checkbox"
                          checked={logoBacker}
                          onChange={(e) => setLogoBacker(e.target.checked)}
                          disabled={!hasLicense}
                          title={hasLicense ? "" : "Unlock Lifetime Pro to enable"}
                          className="h-4 w-4"
                        />
                        <span>White backer behind logo</span>
                      </div>
                    </Label>
                  </div>
                </div>
              </details>

              {/* Actions */}
              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={generateAll}
                  disabled={busy || rows.length === 0}
                  className="btn btn-primary disabled:cursor-not-allowed"
                >
                  {busy ? 'Working…' : 'Generate ZIP'}
                </button>
                <div className="text-xs" aria-live="polite">{msg}</div>
              </div>

              {/* Labels (Pro) */}
              <details open={openSection === 4} className="section">
                <summary className="section-summary" onClick={(e) => { e.preventDefault(); setOpenSection(openSection === 4 ? 0 : 4); }}>4) Label Sheets (Pro)</summary>
                <div className="section-body">
                  <LabelSheetPanel
                    hasLicense={hasLicense}
                    busy={labelsBusy}
                    frameSelected={frameSelected}
                    selectedLabelKey={selectedLabelKey}
                    onChangeSelectedLabelKey={(k) => { setSelectedLabelKey(k); setUserOverrodeLabel(true); }}
                    onResetDefault={() => { setUserOverrodeLabel(false); const def = getDefaultPreset(frameSelected ? 'framed' : 'square'); setSelectedLabelKey(def.key); }}
                    onGenerate={generateLabels}
                  />
                  
                  <div className="text-xs mt-2" aria-live="polite">{labelsMsg}</div>
                </div>
              </details>
            </div>

            {/* Right: Preview */}
            <aside className="preview-panel">
              <h2 className="text-xl font-semibold">Preview</h2>
              <div className="text-xs">
                {rows[0]?.url ? `From first row: ${rows[0].url}` : 'Using demo URL'}
              </div>
              <div className="preview-container">
                <div className="flex justify-center w-full">
                  {previewSRC ? (
                    <div className={`qr-stage ${frameSelected ? 'qr-stage--framed' : 'qr-stage--plain'}`}>
                      <img
                        alt="QR preview"
                        src={previewSRC}
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500">Waiting for settings…</div>
                  )}
                </div>
              </div>

              {/* AdSense box directly under the preview image (zero-CLS). */}
              {shouldShowAds && (
                <AdSenseBoxAd
                  client={ADSENSE_CLIENT}
                  slot={ADSENSE_SLOT_BOX}
                  prefer336
                />
              )}

              <div className="text-xs mt-2">Tip: Use higher ECC (Q/H) when placing a larger center logo.</div>
            </aside>
          </div>
        </main>
        <footer className="app-version">
          <div className="version-text">V1.1</div>
        </footer>
      </div>

      {/* Ads are gated off on the App/tool route. See note above. */}
    </div>
  );
}

/* ---------- Helpers (UI) ---------- */
function Label({ children, className, htmlFor, style, ...rest }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label className={className} htmlFor={htmlFor} style={style} {...rest}>
      {children}
    </label>
  );
}

/* ---------- Helpers (logic) ---------- */
function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}
function pad4(n: number) {
  return n.toString().padStart(4, "0");
}
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function fileToDataURL(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onerror = () => rej(new Error("read failed"));
    fr.onload = () => res(String(fr.result));
    fr.readAsDataURL(file);
  });
}

type Palette = { name: string; colors: { background: string; modules: string; eyeOuter: string; eyeInner: string } };
const PALETTES: Palette[] = [
  { name: "Monochrome", colors: { background: "#FFFFFF", modules: "#000000", eyeOuter: "#000000", eyeInner: "#000000" } },
  { name: "High Contrast", colors: { background: "#FFFFFF", modules: "#111827", eyeOuter: "#111827", eyeInner: "#111827" } },
  { name: "Brand Blue", colors: { background: "#FFFFFF", modules: "#0F172A", eyeOuter: "#2563EB", eyeInner: "#0F172A" } },
];

const LIGHT_PALETTE = { background: "#FFFFFF", modules: "#000000", eyeOuter: "#000000", eyeInner: "#000000" };
const DARK_PALETTE = { background: "#111827", modules: "#F9FAFB", eyeOuter: "#F9FAFB", eyeInner: "#F9FAFB" };

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.trim().toLowerCase().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const num = parseInt(h, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function relLuminance(rgb: { r: number; g: number; b: number }): number {
  const srgb = [rgb.r, rgb.g, rgb.b].map((v) => v / 255);
  const lin = srgb.map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

function wcagContrastRatio(hex1: string, hex2: string): number {
  const a = hexToRgb(hex1);
  const b = hexToRgb(hex2);
  if (!a || !b) return 0;
  const L1 = relLuminance(a) + 0.05;
  const L2 = relLuminance(b) + 0.05;
  const ratio = L1 > L2 ? L1 / L2 : L2 / L1;
  return ratio;
}

/* ---------- SVG + image pipeline ---------- */
function dataURLToString(dataURL: string): string {
  const m = dataURL.match(/^data:([^,]*),(.*)$/);
  if (!m) return "";
  const meta = m[1] || "";
  const raw = m[2] || "";
  const isBase64 = /;base64/i.test(meta);
  try {
    return isBase64 ? decodeURIComponent(escape(atob(raw))) : decodeURIComponent(raw);
  } catch {
    try {
      return atob(raw);
    } catch {
      return raw;
    }
  }
}

async function svgToCanvas(svgString: string, px = 640): Promise<HTMLCanvasElement> {
  const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const img = await loadImage(url);
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = Math.round(px * (img.height / Math.max(1, img.width)));
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  try { URL.revokeObjectURL(url); } catch {}
  return canvas;
}

async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  const blob: Blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b as Blob), "image/png"));
  return blob;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error("Failed to load image"));
    img.src = url;
  });
}
